import { Button, Grid, Typography } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ArticleDataFragment } from '../../graphql/models';
import { GenericCard } from '../common/layout/GenericCard';
import { ResourceCardTagsSection } from '../common/resources/card/ResourceCardTagsSection';
import { ResourceCardTranslationLinks } from '../common/resources/card/ResourceCardTranslationLinks';
import { ResourceCardOriginalLanguageSection } from '../common/resources/card/ResourceCardOriginalLanguageSection';
import { useRoutes } from '../../common/routing/routes-hook';

interface Props {
  article: ArticleDataFragment;
}

export const ArticleCard = ({ article }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { routes } = useRoutes();

  const { lang: activeLanguage } = useParams<{ lang: string }>();

  const {
    content: contentList,
    originalLanguage,
    year,
    doi,
    id,
    venue,
    externalLink,
    tags,
  } = article;

  const translationLinks = contentList
    .filter((content) => content.language !== article.originalLanguage)
    .flatMap((content) => ({
      url: routes.articleResult.pathWithArguments({
        id: article.id,
        lang: content.language,
      }),
      language: content.language,
    }));

  const originalLanguageLinkData = {
    url: routes.articleResult.pathWithArguments({
      id: id,
      lang: originalLanguage,
    }),
    language: originalLanguage,
  };

  const originalLanguageIsAccessible =
    contentList
      .map((content) => content.language)
      .includes(article.originalLanguage) &&
    article.originalLanguage !== activeLanguage;

  return (
    <GenericCard title={t('phrases.additionalInformation')}>
      <Grid container item xs={12} justify="space-between" alignItems="center">
        <Typography color="textSecondary">{t('commons.year')}</Typography>
        <Typography color="textPrimary">{year}</Typography>
      </Grid>
      <Grid container item xs={12} justify="space-between" alignItems="center">
        <Typography color="textSecondary">DOI</Typography>
        <Typography color="textPrimary"> {doi}</Typography>
      </Grid>
      <ResourceCardOriginalLanguageSection
        originalLanguageLink={originalLanguageLinkData}
        isAccessible={originalLanguageIsAccessible}
      />
      <ResourceCardTranslationLinks translationLinks={translationLinks} />
      <Grid container item xs={12} justify="space-between" alignItems="center">
        <Typography color="textSecondary">{t('commons.venue')}</Typography>
        <Typography color="textPrimary">{venue}</Typography>
      </Grid>
      <Grid container item xs={12} justify="space-between" alignItems="center">
        <Typography color="textSecondary">Link</Typography>
        <Button href={externalLink} target="_blank">
          {t('phrases.readFullText')}
        </Button>
      </Grid>
      <ResourceCardTagsSection tags={tags || []} />
    </GenericCard>
  );
};
